<template>
  <div class="flex items-center gap-2">
    <el-popover :visible="visible" :width="320">
      <div :dir="$ctx.locale == 'en' ? 'ltr' : 'rtl'" class="flex flex-col gap-4">
        <div class="w-full flex items-center justify-between">
          <h1 class="text-l font-bold">{{ t('itemPrice') }}</h1>
          <h1 class="text-l font-bold">
            {{ formatMoney(currentPrice, 'IQD') }}
          </h1>
        </div>

        <el-divider class="!my-2" />

        <el-radio-group class="w-full flex" v-model="discountType">
          <el-radio-button class="flex-1" label="percentage" border>
            {{ t('percentage') }}
          </el-radio-button>
          <el-radio-button class="flex-1" label="amount" border>
            {{ t('amount') }}
          </el-radio-button>
        </el-radio-group>

        <div v-if="discountType === 'percentage'" class="w-full flex items-center justify-between">
          <h1 class="text-l font-bold">{{ t('percentage') }}</h1>
          <div class="flex items-center">
            <span class="mx-4 text-l font-bold">%</span>
            <el-input-number
              v-model="discountPercentage"
              :step="1"
              size="default"
              :min="0"
              :max="100"
              :controls="false"
              class="!w-24"
            />
          </div>
        </div>

        <div v-if="discountType === 'amount'" class="w-full flex items-center justify-between">
          <h1 class="text-l font-bold">{{ t('amount') }}</h1>
          <div class="flex items-center">
            <MoneyInput
              class="!w-32"
              currency="IQD"
              v-model="discountAmount"
            />
          </div>
        </div>

        <div class="w-full flex items-center justify-between bg-gray-200 p-3 rounded">
          <h1 class="text-l font-bold">{{ t('priceAfterDiscount') }}</h1>
          <h1 class="text-blue-500 text-l font-bold">
            {{ formatMoney(priceAfterDiscount, 'IQD') }}
          </h1>
        </div>

        <div class="flex justify-end gap-2">
          <el-button v-if="hasDiscount" text @click="clearDiscount" type="warning">
            {{ t('clearDiscount') }}
          </el-button>
          <el-button text @click="visible = false">{{ t('cancel') }}</el-button>
          <el-button type="primary" @click="confirm">
            {{ t('ok') }}
          </el-button>
        </div>
      </div>

      <template #reference>
        <div>
          <button
            v-if="orderItem.item?.discountEligible"
            class="bg-gray-300 hover:bg-gray-500 text-black hover:text-white font-bold py-1 px-2 rounded"
            :class="{ '!bg-green-200': hasDiscount }"
            type="button"
            @click="visible = !visible"
          >
            <font-awesome-icon
              :class="{ 'text-green-600': hasDiscount }"
              class="cursor-pointer outline-none"
              icon="percentage"
            />
          </button>
        </div>
      </template>
    </el-popover>
  </div>
</template>

<script setup lang="ts">
import { computed, defineEmits, defineProps, onBeforeMount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatMoney } from '@/utils/money'
import { OrderItem } from '@/models/sales/Order'
import MoneyInput from '@/components/form/MoneyInput.vue'

const props = defineProps<{
  orderItem: OrderItem
}>()

const emit = defineEmits<{
  (e: 'onItemDiscount', data: { discountType: string; discountAmount: number; discountTotal: number } | null): void
}>()

const messages = {
  en: {
    itemPrice: 'Item Price',
    percentage: 'Percentage',
    amount: 'Amount',
    priceAfterDiscount: 'Price After Discount',
    ok: 'Confirm',
    cancel: 'Cancel',
    clearDiscount: 'Clear Discount'
  },
  ar: {
    itemPrice: 'سعر المنتج',
    percentage: 'النسبة المؤوية',
    amount: 'المبلغ',
    priceAfterDiscount: 'السعر بعد الخصم',
    ok: 'تأكيد',
    cancel: 'الغاء',
    clearDiscount: 'ازالة الخصم'
  }
}

const { t } = useI18n({ messages, useScope: 'global' })

const visible = ref(false)
const discountType = ref<'percentage' | 'amount'>('percentage')
const discountPercentage = ref(0)
const discountAmount = ref(0)

const hasDiscount = computed(() => {
  return props.orderItem.itemDiscountTotal > 0
})

// Get base price (before item discount) by adding back the discount if applied
const currentPrice = computed(() => {
  const oi = props.orderItem
  return (oi.itemSalePriceAmount || 0) + (oi.itemDiscountTotal || 0)
})

const priceAfterDiscount = computed(() => {
  const itemPrice = currentPrice.value
  if (discountType.value === 'percentage') {
    const discount = Math.floor((itemPrice * discountPercentage.value) / 100)
    return Math.max(itemPrice - discount, 0)
  } else {
    return Math.max(itemPrice - discountAmount.value, 0)
  }
})

onBeforeMount(() => {
  loadExistingDiscount()
})

// Load existing discount when dialog opens
watch(visible, (isVisible) => {
  if (isVisible) {
    loadExistingDiscount()
  }
})

watch(
  () => props.orderItem.itemDiscountType,
  () => {
    loadExistingDiscount()
  }
)

// Reset values when switching discount type
watch(discountType, (newType, oldType) => {
  if (newType !== oldType) {
    discountPercentage.value = 0
    discountAmount.value = 0
  }
})

function loadExistingDiscount() {
  if (props.orderItem.itemDiscountType) {
    discountType.value = props.orderItem.itemDiscountType as 'percentage' | 'amount'
    if (discountType.value === 'percentage') {
      discountPercentage.value = props.orderItem.itemDiscountAmount || 0
    } else {
      discountAmount.value = props.orderItem.itemDiscountAmount || 0
    }
  } else {
    discountType.value = 'percentage'
    discountPercentage.value = 0
    discountAmount.value = 0
  }
}

function confirm() {
  visible.value = false
  const itemPrice = currentPrice.value
  let discountTotal = 0

  if (discountType.value === 'percentage') {
    discountTotal = Math.floor((itemPrice * discountPercentage.value) / 100)
    emit('onItemDiscount', {
      discountType: 'percentage',
      discountAmount: discountPercentage.value,
      discountTotal: discountTotal
    })
  } else {
    discountTotal = Math.min(discountAmount.value, itemPrice)
    emit('onItemDiscount', {
      discountType: 'amount',
      discountAmount: discountAmount.value,
      discountTotal: discountTotal
    })
  }
}

function clearDiscount() {
  discountPercentage.value = 0
  discountAmount.value = 0
  visible.value = false
  emit('onItemDiscount', null)
}
</script>
